import * as XLSX from 'xlsx';

/**
 * Bajar datos como Excel. Un solo lugar, para que todas las descargas del producto salgan
 * iguales y con las mismas reglas.
 *
 * Se arma **en el navegador** con la misma librería que ya se usa para leer los archivos de
 * BET: no hay endpoint de descarga ni el Worker gasta tiempo generando planillas, y los datos
 * ya están en la pantalla.
 *
 * ⚠️ Las reglas que importan, y que son las que hacen que la planilla sirva para volver a
 * subirla:
 *
 *  · **Los números viajan como números**, no como texto. Un `"1.234"` con separador de miles
 *    entra a Excel como cadena, no se puede sumar, y al re-subirlo el importador lo lee mal.
 *    Por eso acá NO se usan los formateadores de pantalla (`formatNumber`, `formatCop`).
 *  · **Un dato que falta va VACÍO**, nunca en cero ni con un guion: un 0 se lee como "cuesta
 *    cero" y un "—" convierte la columna en texto. Es el mismo criterio que la pantalla usa
 *    al decir «sin costo» en vez de mostrar 0.
 *  · **Los encabezados son los que el importador espera**. Si se renombra una columna acá,
 *    hay que renombrarla también en `src/server/routes/products.ts`.
 */

/** Una hoja del libro: su nombre y sus filas ya como objetos {encabezado: valor}. */
export interface Hoja {
  nombre: string;
  filas: Record<string, unknown>[];
  /** Ancho de cada columna en caracteres, en el orden de los encabezados. */
  anchos?: number[];
}

/** `2026-09-01` — para el nombre del archivo, que así ordena solo en la carpeta. */
function hoy(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Arma el libro y lo descarga.
 *
 * ⚠️ El nombre de la hoja se recorta a 31 caracteres porque **Excel no abre un archivo con una
 * hoja de nombre más largo**: no avisa, simplemente lo da por corrupto.
 */
export function descargarExcel(nombreArchivo: string, hojas: Hoja[]): void {
  const libro = XLSX.utils.book_new();
  for (const hoja of hojas) {
    const ws = XLSX.utils.json_to_sheet(hoja.filas);
    if (hoja.anchos) ws['!cols'] = hoja.anchos.map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(libro, ws, hoja.nombre.slice(0, 31));
  }
  XLSX.writeFile(libro, `${nombreArchivo}-${hoy()}.xlsx`);
}

/** Un número, o vacío si no hay dato. Ver la regla de arriba: cero y "—" mienten. */
export function num(v: number | null | undefined, mostrarCero = true): number | '' {
  if (v == null || Number.isNaN(v)) return '';
  if (!mostrarCero && v === 0) return '';
  return v;
}

/** Un texto, o vacío. Evita que un `null` salga como la palabra "null" en la celda. */
export function txt(v: string | null | undefined): string {
  return v ?? '';
}
